import { KeyRound, Lock, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';

import { EmployeePage } from '@/components/employee/EmployeePage';
import { Button } from '@/components/ui/button';
import { ErrorState } from '@/components/ui/empty-state';
import { Field, FormError } from '@/components/ui/field';
import {
  useEmployeeNotificationPreferences,
  useMyProfile,
  useSetEmployeeNotificationPreference,
  useSetEmployeeRecoveryEmail,
  useUpdateMyProfile,
} from '@/hooks/employee-portal';
import { ApiError } from '@/lib/api';
import { formatDay } from '@/lib/dates';
import { useToast } from '@/lib/toast-context';
import {
  EMPLOYEE_NOTIFICATION_TYPES,
  EMPLOYEE_NOTIFICATION_TYPE_HINTS,
  EMPLOYEE_NOTIFICATION_TYPE_LABELS,
  type EmployeeProfileView,
} from '@shared/schemas/employee-portal';

/*
 * /employee/profile — ROADMAP.md §6.7.
 *
 * **A cleaner edits their own name, phone and address; a client edits nothing.**
 * The asymmetry is deliberate and it is the prototype's (FINDINGS.md §11.3): a
 * client's service address is where somebody is being *sent*, and changing it
 * under a booked job sends a cleaner to an empty house. A home address is the
 * employee's own detail.
 *
 * What cannot change says so rather than being hidden — design-system.md §7,
 * "explain, don't hide", and FINDINGS.md §11.5 singles out "Skills and capacity
 * are managed by your administrator" as exactly the right way to show a
 * read-only field, because it says who to ask.
 *
 * The pay rate is not on this screen at all. business-rules.md §2 would allow an
 * employee their own, but the office answers pay questions and the view behind
 * this never reads the column.
 */

export function ProfilePage() {
  const profile = useMyProfile();

  if (profile.error) {
    return (
      <EmployeePage title="My Profile">
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          <ErrorState
            title="We could not load your profile"
            hint="Something went wrong on our side. Try again, and ring the office if it keeps happening."
            onRetry={() => {
              void profile.refetch();
            }}
          />
        </div>
      </EmployeePage>
    );
  }

  const data = profile.data;

  return (
    <EmployeePage
      title="My Profile"
      description="Your details, and how we let you know about new jobs."
    >
      {profile.isPending || !data ? (
        <div aria-busy="true" className="space-y-4">
          <span className="sr-only">Loading your profile</span>
          {[0, 1, 2].map((key) => (
            <div
              key={key}
              aria-hidden="true"
              className="h-52 animate-pulse rounded-lg bg-ink-100"
            />
          ))}
        </div>
      ) : (
        <div className="space-y-4">
          <DetailsCard profile={data} />
          <LoginCard profile={data} />
          <AdminManagedCard profile={data} />
          <NotificationsCard />
        </div>
      )}
    </EmployeePage>
  );
}

/** Name, phone and home address — the editable half. */
function DetailsCard({ profile }: { profile: EmployeeProfileView }) {
  const toast = useToast();
  const save = useUpdateMyProfile();

  const [form, setForm] = useState({
    firstName: profile.firstName,
    lastName: profile.lastName,
    phone: profile.phone ?? '',
    address: profile.address ?? '',
    city: profile.city ?? '',
    state: profile.state ?? '',
    zip: profile.zip ?? '',
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate(
      {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        address: form.address,
        city: form.city,
        state: form.state,
        zip: form.zip,
      },
      {
        onSuccess: () => {
          toast.success('Your details are saved.');
        },
      },
    );
  }

  function set(key: keyof typeof form) {
    return (event: { target: { value: string } }) => {
      setForm((current) => ({ ...current, [key]: event.target.value }));
    };
  }

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">Your details</h2>
      <p className="mt-1 text-meta text-ink-500">
        Keep these current — the office rings this number when a job changes.
      </p>

      <form onSubmit={submit} className="mt-4 space-y-4">
        {error ? <FormError>{error.message}</FormError> : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label="First name"
            required
            value={form.firstName}
            onChange={set('firstName')}
            error={fieldErrors.firstName}
          />
          <Field
            label="Last name"
            required
            value={form.lastName}
            onChange={set('lastName')}
            error={fieldErrors.lastName}
          />
        </div>

        <Field
          label="Phone"
          type="tel"
          value={form.phone}
          onChange={set('phone')}
          error={fieldErrors.phone}
        />

        <Field
          label="Home address"
          value={form.address}
          onChange={set('address')}
          hint="Used for travel planning. Nobody is sent here."
          error={fieldErrors.address}
        />

        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="City" value={form.city} onChange={set('city')} error={fieldErrors.city} />
          <Field
            label="State"
            value={form.state}
            onChange={set('state')}
            error={fieldErrors.state}
          />
          <Field label="ZIP" value={form.zip} onChange={set('zip')} error={fieldErrors.zip} />
        </div>

        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </form>
    </section>
  );
}

/**
 * The login, and the recovery address.
 *
 * **"Cannot change" is the prototype's wording and it is good posture** — a
 * login identity that moves is one audit rows and password resets stop agreeing
 * about. There is no endpoint for it either; this is not a disabled input in
 * front of a live route.
 */
function LoginCard({ profile }: { profile: EmployeeProfileView }) {
  const toast = useToast();
  const save = useSetEmployeeRecoveryEmail();
  const [recovery, setRecovery] = useState(profile.recoveryEmail ?? '');

  const error = save.error instanceof ApiError ? save.error : null;

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="flex items-center gap-1.5 text-h3 text-ink-900">
        <Lock aria-hidden="true" className="size-4 text-ink-500" />
        How you sign in
      </h2>

      <dl className="mt-3 space-y-2 text-body">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-ink-500">{profile.loginEmail ? 'Email' : 'Username'}</dt>
          <dd className="text-ink-900">
            {profile.loginEmail ?? profile.username}
            <span className="ml-2 rounded-full bg-ink-100 px-2 py-0.5 text-meta text-ink-700">
              Cannot change
            </span>
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-ink-300 pt-4">
        <h3 className="flex items-center gap-1.5 text-label text-ink-900">
          <ShieldCheck aria-hidden="true" className="size-4 text-ink-500" />
          Recovery email
        </h3>
        <p className="mt-1 text-meta text-ink-500">
          {profile.loginEmail === null
            ? /*
               The P9 case, and for cleaners it is the normal one rather than the
               edge: Honeycomb hires without a work email, so a username-only
               account with no recovery address can never be reset and never be
               contacted.
              */
              'You sign in with a username, so this is the only way to get back in if you forget your password. Without one, the office has to reset it for you.'
            : 'A second address we can use if you lose access to your email.'}
        </p>

        <form
          className="mt-3 flex flex-col gap-2 sm:flex-row"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate(recovery, {
              onSuccess: () => {
                toast.success('Recovery email saved.');
              },
            });
          }}
        >
          <input
            type="email"
            value={recovery}
            onChange={(event) => {
              setRecovery(event.target.value);
            }}
            placeholder="you@example.com"
            aria-label="Recovery email"
            className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body sm:max-w-xs"
          />
          <Button type="submit" variant="outline" disabled={save.isPending || recovery === ''}>
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
        </form>

        {error ? (
          <div className="mt-2">
            <FormError>{error.message}</FormError>
          </div>
        ) : null}

        {profile.recoveryEmail === null ? (
          <p className="mt-2 text-meta text-ink-500">
            {/* An absent value as a sentence, never a dash (P1). */}
            <span className="italic">
              Not set — add one to recover your account if you lose access
            </span>
          </p>
        ) : null}
      </div>

      <div className="mt-4 border-t border-ink-300 pt-4">
        <Link
          to="/change-password"
          className="inline-flex min-h-touch items-center gap-2 text-label text-bark-700 underline-offset-2 hover:underline"
        >
          <KeyRound aria-hidden="true" className="size-4" />
          Change your password
        </Link>
      </div>
    </section>
  );
}

/**
 * Skills, capacity and position — read-only, and it says who to ask.
 *
 * FINDINGS.md §11.5: "Skills and capacity are managed by your administrator" is
 * exactly the right way to show a field somebody cannot edit. It is preserved
 * word for word in spirit.
 */
function AdminManagedCard({ profile }: { profile: EmployeeProfileView }) {
  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">Your role</h2>
      <p className="mt-1 text-meta text-ink-500">
        Skills and capacity are managed by your administrator. Ask the office if any of this is
        wrong.
      </p>

      <dl className="mt-3 space-y-2.5 text-body">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-ink-500">Position</dt>
          <dd className="text-ink-900">
            {profile.positionName ?? <span className="italic text-ink-500">Not set</span>}
          </dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-ink-500">Most jobs a day</dt>
          {/* Zero is a number; this one is never zero, but it is still a figure
              rather than a dash. */}
          <dd className="text-ink-900">{profile.maxJobsPerDay}</dd>
        </div>
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <dt className="text-ink-500">Started</dt>
          <dd className="text-ink-900">
            {profile.hireDate ? (
              formatDay(profile.hireDate)
            ) : (
              <span className="italic text-ink-500">Not recorded</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-ink-500">Skills</dt>
          <dd className="mt-1 flex flex-wrap gap-1.5">
            {profile.skills.length === 0 ? (
              <span className="text-body italic text-ink-500">None recorded yet</span>
            ) : (
              profile.skills.map((entry) => (
                <span
                  key={entry.id}
                  className="rounded-full bg-honey-50 px-2.5 py-0.5 text-meta text-bark-900"
                >
                  {entry.name}
                </span>
              ))
            )}
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * Notification preferences, employee-scoped.
 *
 * Three types, different from the client's three — types are role-scoped
 * (data-model.md §8) and the server's enum is the fence. **Absence means
 * enabled**, so a switch exists in order to turn something off.
 *
 * Email and push are shown and explained rather than hidden, and the server
 * refuses to *store* an enabled non-in-app preference: a stored promise the app
 * cannot keep is one somebody notices by not hearing from us.
 */
function NotificationsCard() {
  const toast = useToast();
  const preferences = useEmployeeNotificationPreferences();
  const save = useSetEmployeeNotificationPreference();

  const disabled = new Set(preferences.data?.disabled ?? []);

  return (
    <section className="rounded-lg border border-ink-300 bg-white p-4 shadow-card">
      <h2 className="text-h3 text-ink-900">Letting you know</h2>
      <p className="mt-1 text-meta text-ink-500">
        These show up in the app. Email and text are not sending yet — you will be able to turn them
        on here when they are.
      </p>

      {preferences.error ? (
        <div className="mt-3">
          <ErrorState
            title="We could not load your settings"
            onRetry={() => {
              void preferences.refetch();
            }}
          />
        </div>
      ) : (
        <ul className="mt-3 divide-y divide-ink-300">
          {EMPLOYEE_NOTIFICATION_TYPES.map((type) => {
            const key = `${type}:in_app`;
            const isOn = !disabled.has(key);

            return (
              <li key={type} className="flex items-start justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-body text-ink-900">
                    {EMPLOYEE_NOTIFICATION_TYPE_LABELS[type]}
                  </p>
                  <p className="mt-0.5 text-meta text-ink-500">
                    {EMPLOYEE_NOTIFICATION_TYPE_HINTS[type]}
                  </p>
                </div>

                <label className="flex min-h-touch shrink-0 cursor-pointer items-center gap-2 text-label">
                  <input
                    type="checkbox"
                    checked={isOn}
                    disabled={preferences.isPending || save.isPending}
                    onChange={(event) => {
                      save.mutate(
                        { type, channel: 'in_app', isEnabled: event.target.checked },
                        {
                          onError: (error) => {
                            toast.error(
                              error instanceof ApiError
                                ? error.message
                                : 'Could not save that setting.',
                            );
                          },
                        },
                      );
                    }}
                    className="size-5 accent-honey-400"
                  />
                  <span className="text-ink-700">{isOn ? 'On' : 'Off'}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
